import React, { useState, useEffect } from 'react';
import { usuariosAPI, authAPI, plantillasEmailAPI, etiquetasHerramientasAPI } from '../services/api';
import './Perfil.css';

function Perfil() {
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellidos: '',
    nombre_empresa: '',
    email: '',
    password: '',
    direccion: '',
    codigo_postal: '',
    ciudad: '',
    provincia: '',
    telefono: '',
    nif_cif: '',
    banco_nombre: '',
    banco_iban: '',
    moneda_facturacion: 'EUR',
    posicion_moneda_facturacion: 'after',
    formato_numero_factura: '',
    navbar_background_color: '',
    navbar_text_color: '',
    recordatorios_cita_automaticos: false,
    einvoice_habilitado: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [faviconPreview, setFaviconPreview] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  // Plantillas de email (factura / presupuesto / recordatorio_cita)
  const facturacionActiva = user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1);
  const [plantillaTipo, setPlantillaTipo] = useState(facturacionActiva ? 'factura' : 'recordatorio_cita');
  const [plantillaAsunto, setPlantillaAsunto] = useState('');
  const [plantillaCuerpo, setPlantillaCuerpo] = useState('');
  const [plantillaSaving, setPlantillaSaving] = useState(false);
  // Etiquetas técnicas/herramientas/ejercicios (por coach)
  const [etiquetasHerramientas, setEtiquetasHerramientas] = useState([]);
  const [showEtiquetaForm, setShowEtiquetaForm] = useState(false);
  const [editingEtiqueta, setEditingEtiqueta] = useState(null);
  const [formEtiqueta, setFormEtiqueta] = useState({ nombre: '', orden: 0 });

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    try {
      setLoading(true);
      const response = await authAPI.check();
      if (response.data.authenticated && response.data.user) {
        const userData = response.data.user;
        setUser(userData);
        // Cargar datos completos del usuario
        const userResponse = await usuariosAPI.getById(userData.id);
        const fullUserData = userResponse.data;
        setFormData({
          nombre: fullUserData.nombre || '',
          apellidos: fullUserData.apellidos || '',
          nombre_empresa: fullUserData.nombre_empresa || '',
          email: fullUserData.email || '',
          password: '', // No mostrar contraseña
          direccion: fullUserData.direccion || '',
          codigo_postal: fullUserData.codigo_postal || '',
          ciudad: fullUserData.ciudad || '',
          provincia: fullUserData.provincia || '',
          telefono: fullUserData.telefono || '',
          nif_cif: fullUserData.nif_cif || '',
          banco_nombre: fullUserData.banco_nombre || '',
          banco_iban: fullUserData.banco_iban || '',
          moneda_facturacion: fullUserData.moneda_facturacion || 'EUR',
          posicion_moneda_facturacion: fullUserData.posicion_moneda_facturacion || 'after',
          formato_numero_factura: fullUserData.formato_numero_factura || '',
          navbar_background_color: fullUserData.navbar_background_color || '',
          navbar_text_color: fullUserData.navbar_text_color || '',
          recordatorios_cita_automaticos: !!fullUserData.recordatorios_cita_automaticos,
          einvoice_habilitado: !!fullUserData.einvoice_habilitado,
        });
        // Cargar preview del logo si existe
        if (fullUserData.logo) {
          setLogoPreview(fullUserData.logo);
        }
        // Cargar preview del favicon si existe
        if (fullUserData.favicon) {
          setFaviconPreview(fullUserData.favicon);
        }
      }
    } catch (err) {
      setError('Error al cargar los datos del perfil');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const loadEtiquetasHerramientas = React.useCallback(async () => {
    if (user?.rol === 'admin') return;
    try {
      const r = await etiquetasHerramientasAPI.getAll();
      setEtiquetasHerramientas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setEtiquetasHerramientas([]);
    }
  }, [user?.rol]);

  useEffect(() => {
    if (user?.rol !== 'admin') loadEtiquetasHerramientas();
  }, [user?.rol, loadEtiquetasHerramientas]);

  const handleNuevaEtiqueta = () => {
    setEditingEtiqueta(null);
    setFormEtiqueta({ nombre: '', orden: etiquetasHerramientas.length });
    setShowEtiquetaForm(true);
  };

  const handleEditEtiqueta = (e) => {
    setEditingEtiqueta(e);
    setFormEtiqueta({ nombre: e.nombre, orden: Number(e.orden) });
    setShowEtiquetaForm(true);
  };

  const handleSaveEtiqueta = async () => {
    if (!formEtiqueta.nombre.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    setError(null);
    try {
      if (editingEtiqueta) {
        await etiquetasHerramientasAPI.update(editingEtiqueta.id, formEtiqueta);
        setSuccess('Etiqueta actualizada');
      } else {
        await etiquetasHerramientasAPI.create(formEtiqueta);
        setSuccess('Etiqueta creada');
      }
      setShowEtiquetaForm(false);
      setEditingEtiqueta(null);
      loadEtiquetasHerramientas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDeleteEtiqueta = async (id) => {
    if (!window.confirm('¿Eliminar esta etiqueta?')) return;
    try {
      await etiquetasHerramientasAPI.delete(id);
      setSuccess('Etiqueta eliminada');
      loadEtiquetasHerramientas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const loadPlantilla = React.useCallback(async (tipo) => {
    try {
      const r = await plantillasEmailAPI.getByTipo(tipo);
      const d = r.data;
      setPlantillaAsunto(d?.asunto ?? '');
      setPlantillaCuerpo(d?.cuerpo_html ?? '');
    } catch (e) {
      setPlantillaAsunto('');
      setPlantillaCuerpo('');
    }
  }, []);

  useEffect(() => {
    if (plantillaTipo) loadPlantilla(plantillaTipo);
  }, [plantillaTipo, loadPlantilla]);

  const handleSavePlantilla = async () => {
    setPlantillaSaving(true);
    setError(null);
    try {
      await plantillasEmailAPI.save(plantillaTipo, { asunto: plantillaAsunto, cuerpo_html: plantillaCuerpo });
      setSuccess('Plantilla de email guardada');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar plantilla');
    } finally {
      setPlantillaSaving(false);
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo
      if (!file.type.match('image.*')) {
        setError('Por favor, selecciona un archivo de imagen');
        return;
      }
      
      // Validar tamaño (máximo 2MB)
      if (file.size > 2 * 1024 * 1024) {
        setError('El archivo es demasiado grande. Máximo 2MB');
        return;
      }
      
      setLogoFile(file);
      setError(null);
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de archivo (favicon puede ser ICO, PNG, SVG, etc.)
      const allowedTypes = ['image/x-icon', 'image/vnd.microsoft.icon', 'image/png', 'image/jpeg', 'image/gif', 'image/svg+xml'];
      if (!allowedTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.ico')) {
        setError('Por favor, selecciona un archivo de favicon válido (ICO, PNG, SVG, etc.)');
        return;
      }
      
      // Validar tamaño (máximo 1MB para favicon)
      if (file.size > 1 * 1024 * 1024) {
        setError('El archivo es demasiado grande. Máximo 1MB');
        return;
      }
      
      setFaviconFile(file);
      setError(null);
      
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFaviconPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nombre || !formData.apellidos) {
      setError('Nombre y apellidos son requeridos');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Preparar datos para enviar
      const data = {
        nombre: formData.nombre,
        apellidos: formData.apellidos,
        nombre_empresa: formData.nombre_empresa || null,
        email: formData.email || null,
        direccion: formData.direccion || null,
        codigo_postal: formData.codigo_postal || null,
        ciudad: formData.ciudad || null,
        provincia: formData.provincia || null,
        telefono: formData.telefono || null,
        nif_cif: formData.nif_cif || null,
        banco_nombre: formData.banco_nombre || null,
        banco_iban: formData.banco_iban || null,
        moneda_facturacion: formData.moneda_facturacion || 'EUR',
        posicion_moneda_facturacion: formData.posicion_moneda_facturacion || 'after',
        formato_numero_factura: formData.formato_numero_factura?.trim()
          ? formData.formato_numero_factura.trim()
          : null,
        navbar_background_color: formData.navbar_background_color || null,
        navbar_text_color: formData.navbar_text_color || null,
        recordatorios_cita_automaticos: formData.recordatorios_cita_automaticos,
        einvoice_habilitado: formData.einvoice_habilitado,
      };
      
      // Solo incluir password si se proporcionó uno nuevo
      if (formData.password) {
        data.password = formData.password;
      }
      
      // Si hay un logo nuevo, subirlo primero
      if (logoFile) {
        try {
          setUploadingLogo(true);
          const response = await usuariosAPI.uploadLogo(user.id, logoFile);
          if (response.data.success) {
            data.logo = response.data.logo;
          }
        } catch (error) {
          setError('Error al subir el logo: ' + (error.response?.data?.error || error.message));
          setUploadingLogo(false);
          setSaving(false);
          return;
        } finally {
          setUploadingLogo(false);
        }
      }
      
      // Si hay un favicon nuevo, subirlo primero
      if (faviconFile) {
        try {
          setUploadingFavicon(true);
          const response = await usuariosAPI.uploadFavicon(user.id, faviconFile);
          if (response.data.success) {
            data.favicon = response.data.favicon;
          }
        } catch (error) {
          setError('Error al subir el favicon: ' + (error.response?.data?.error || error.message));
          setUploadingFavicon(false);
          setSaving(false);
          return;
        } finally {
          setUploadingFavicon(false);
        }
      }
      
      await usuariosAPI.update(user.id, data);
      
      setSuccess('Perfil actualizado correctamente');
      
      // Limpiar archivos seleccionados
      setLogoFile(null);
      setFaviconFile(null);
      
      // Recargar datos del usuario
      await loadUser();
      
      // Recargar la página para actualizar el logo/favicon en el navbar
      window.location.reload();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar el perfil');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Cargando perfil...</div>;
  }

  if (!user) {
    return <div className="error">No se pudo cargar la información del usuario</div>;
  }

  return (
    <div className="perfil">
      <div className="page-header">
        <h2>Mi Perfil</h2>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="perfil-card">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              value={user.username}
              disabled
              className="disabled-input"
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
              El username no se puede cambiar
            </small>
          </div>

          <div className="form-group">
            <label>Rol</label>
            <input
              type="text"
              value={user.rol === 'admin' ? 'Administrador' : 'Coach'}
              disabled
              className="disabled-input"
            />
          </div>

          <div className="form-group">
            <label>Nombre *</label>
            <input
              type="text"
              name="nombre"
              value={formData.nombre}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Apellidos *</label>
            <input
              type="text"
              name="apellidos"
              value={formData.apellidos}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Nueva Contraseña (dejar vacío para no cambiar)</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Dejar vacío para mantener la contraseña actual"
            />
          </div>

          {user.rol !== 'admin' && (
            <div className="form-group">
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="recordatorios_cita_automaticos"
                  checked={formData.recordatorios_cita_automaticos}
                  onChange={handleChange}
                />
                Enviar recordatorios de sesión automáticamente (24 h antes al cliente)
              </label>
              <small style={{ display: 'block', color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Si está activado, el sistema enviará un email de recordatorio a cada cliente 24 horas antes de su sesión. Se ejecuta al abrir el CRM (no hace falta configurar cron).
              </small>
            </div>
          )}

          {user.rol !== 'admin' && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
            <div className="form-group">
              <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="einvoice_habilitado"
                  checked={formData.einvoice_habilitado}
                  onChange={handleChange}
                />
                Facturación electrónica UE (e-invoice)
              </label>
              <small style={{ display: 'block', color: '#666', fontSize: '12px', marginTop: '4px' }}>
                Si está activado, podrás descargar facturas en formato XML UBL 2.1 (EN 16931) desde el detalle de cada factura. Útil para administraciones y clientes en la UE.
              </small>
            </div>
          )}

          {user.rol !== 'admin' && (
          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #eee' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Plantillas de email (por coach)</h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>
              Cada coach tiene sus propias plantillas. Personaliza el asunto y el cuerpo de los emails al enviar facturas, presupuestos o recordatorios de sesión. Si dejas el cuerpo vacío, se usará la plantilla por defecto del sistema.
            </p>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {(user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
                <button type="button" className={`btn ${plantillaTipo === 'factura' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPlantillaTipo('factura')}>Factura</button>
              )}
              {(user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
                <button type="button" className={`btn ${plantillaTipo === 'presupuesto' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPlantillaTipo('presupuesto')}>Presupuesto</button>
              )}
              <button type="button" className={`btn ${plantillaTipo === 'recordatorio_cita' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setPlantillaTipo('recordatorio_cita')}>Recordatorio de sesión</button>
            </div>
            <div className="form-group">
              <label>Asunto del email</label>
              <input type="text" value={plantillaAsunto} onChange={(e) => setPlantillaAsunto(e.target.value)} placeholder={plantillaTipo === 'factura' ? 'Factura {{numero_factura}} - {{usuario_nombre}}' : plantillaTipo === 'presupuesto' ? 'Presupuesto {{numero_presupuesto}} - {{usuario_nombre}}' : 'Recordatorio: {{titulo_sesion}} - {{usuario_nombre}}'} />
            </div>
            <div className="form-group">
              <label>Cuerpo (HTML)</label>
              <textarea value={plantillaCuerpo} onChange={(e) => setPlantillaCuerpo(e.target.value)} rows={10} placeholder="Puedes usar HTML. Variables: ver abajo." style={{ fontFamily: 'monospace', fontSize: '13px' }} />
            </div>
            <small style={{ display: 'block', color: '#666', fontSize: '12px', marginBottom: '12px' }}>
              {plantillaTipo === 'factura' && 'Variables: {{cliente_nombre}}, {{numero_factura}}, {{fecha_emision}}, {{total}}, {{usuario_nombre}}, {{enlace_pdf}}'}
              {plantillaTipo === 'presupuesto' && 'Variables: {{cliente_nombre}}, {{numero_presupuesto}}, {{fecha_emision}}, {{total}}, {{usuario_nombre}}, {{enlace_pdf}}'}
              {plantillaTipo === 'recordatorio_cita' && 'Variables: {{cliente_nombre}}, {{titulo_sesion}}, {{fecha_hora}}, {{duracion}}, {{modalidad}}, {{estado}}, {{usuario_nombre}}, {{notas}}'}
            </small>
            <button type="button" className="btn btn-primary" onClick={handleSavePlantilla} disabled={plantillaSaving}>
              {plantillaSaving ? 'Guardando…' : 'Guardar plantilla'}
            </button>
          </div>
          )}

          {user.rol !== 'admin' && (
          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #eee' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Etiquetas: Técnicas, herramientas y ejercicios</h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>
              Define las etiquetas predefinidas que podrás seleccionar al editar cada cliente en el campo «Técnicas, herramientas y ejercicios utilizados».
            </p>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleNuevaEtiqueta} style={{ marginBottom: '12px' }}>+ Nueva etiqueta</button>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
              {etiquetasHerramientas.map((e) => (
                <span
                  key={e.id}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '6px 12px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '20px',
                    fontSize: '14px',
                  }}
                >
                  {e.nombre}
                  <button type="button" onClick={() => handleEditEtiqueta(e)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '14px' }} title="Editar">✎</button>
                  <button type="button" onClick={() => handleDeleteEtiqueta(e.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', fontSize: '14px', color: '#dc3545' }} title="Eliminar">×</button>
                </span>
              ))}
              {etiquetasHerramientas.length === 0 && <span style={{ color: '#888', fontSize: '13px' }}>No hay etiquetas. Crea una para usarlas en los clientes.</span>}
            </div>
            {showEtiquetaForm && (
              <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowEtiquetaForm(false)}>
                <div style={{ background: 'white', padding: '24px', borderRadius: '8px', minWidth: '300px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }} onClick={(ev) => ev.stopPropagation()}>
                  <h4 style={{ marginBottom: '16px' }}>{editingEtiqueta ? 'Editar' : 'Nueva'} etiqueta</h4>
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label>Nombre</label>
                    <input type="text" value={formEtiqueta.nombre} onChange={(ev) => setFormEtiqueta((f) => ({ ...f, nombre: ev.target.value }))} placeholder="Ej: PNL, Mindfulness, Coaching ontológico" style={{ width: '100%', padding: '8px' }} />
                  </div>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Orden</label>
                    <input type="number" min="0" value={formEtiqueta.orden} onChange={(ev) => setFormEtiqueta((f) => ({ ...f, orden: Number(ev.target.value) }))} style={{ width: '80px', padding: '8px' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowEtiquetaForm(false)}>Cancelar</button>
                    <button type="button" className="btn btn-primary" onClick={handleSaveEtiqueta}>Guardar</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          )}

          <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '2px solid #eee' }}>
            <h3 style={{ marginBottom: '20px', color: '#2c3e50' }}>Datos Fiscales (para facturas)</h3>
            
            <div className="form-group">
              <label>Nombre de empresa</label>
              <input
                type="text"
                name="nombre_empresa"
                value={formData.nombre_empresa}
                onChange={handleChange}
                placeholder="Ej: Mi empresa de coaching"
              />
              <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                Se mostrará en facturas, portal del cliente y cabecera del panel
              </small>
            </div>

            <div className="form-group">
              <label>NIF/CIF</label>
              <input
                type="text"
                name="nif_cif"
                value={formData.nif_cif}
                onChange={handleChange}
                placeholder="Ej: 12345678A"
              />
            </div>

            <div className="form-group">
              <label>Dirección</label>
              <input
                type="text"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Calle y número"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
              <div className="form-group">
                <label>Código Postal</label>
                <input
                  type="text"
                  name="codigo_postal"
                  value={formData.codigo_postal}
                  onChange={handleChange}
                  placeholder="28001"
                />
              </div>

              <div className="form-group">
                <label>Ciudad</label>
                <input
                  type="text"
                  name="ciudad"
                  value={formData.ciudad}
                  onChange={handleChange}
                  placeholder="Madrid"
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Provincia</label>
                <input
                  type="text"
                  name="provincia"
                  value={formData.provincia}
                  onChange={handleChange}
                  placeholder="Madrid"
                />
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleChange}
                  placeholder="+34 600 000 000"
                />
              </div>
            </div>

            <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
              <h4 style={{ marginBottom: '15px', color: '#2c3e50' }}>Datos de Pago (se muestran en facturas)</h4>
              <div className="form-group">
                <label>Moneda de facturación</label>
                <select
                  name="moneda_facturacion"
                  value={formData.moneda_facturacion}
                  onChange={handleChange}
                >
                  <option value="EUR">EUR - Euro</option>
                  <option value="USD">USD - US Dollar</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="MXN">MXN - Peso Mexicano</option>
                  <option value="COP">COP - Peso Colombiano</option>
                  <option value="ARS">ARS - Peso Argentino</option>
                  <option value="CLP">CLP - Peso Chileno</option>
                  <option value="PEN">PEN - Sol Peruano</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  Esta moneda se usará por defecto al crear nuevas facturas y presupuestos.
                </small>
              </div>
              <div className="form-group">
                <label>Posición del símbolo/moneda</label>
                <select
                  name="posicion_moneda_facturacion"
                  value={formData.posicion_moneda_facturacion}
                  onChange={handleChange}
                >
                  <option value="before">Antes del número (ej: $ 100.00)</option>
                  <option value="after">Después del número (ej: 100.00 EUR)</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  Controla si la moneda se muestra antes o después del importe.
                </small>
              </div>
              {(user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
              <div className="form-group">
                <label>Formato del número de factura (automático)</label>
                <input
                  type="text"
                  name="formato_numero_factura"
                  value={formData.formato_numero_factura}
                  onChange={handleChange}
                  placeholder="{YEAR}-{NUM3}"
                  style={{ fontFamily: 'monospace', fontSize: '14px' }}
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                  Define cómo se genera el número al crear una factura sin indicar número manualmente. Debe incluir <code style={{ background: '#f0f0f0', padding: '2px 4px' }}>{'{YEAR}'}</code> (año) y un contador: <code style={{ background: '#f0f0f0', padding: '2px 4px' }}>{'{NUM}'}</code> o <code style={{ background: '#f0f0f0', padding: '2px 4px' }}>{'{NUM4}'}</code> (ceros a la izquierda). Opcional: <code style={{ background: '#f0f0f0', padding: '2px 4px' }}>{'{SERIE}'}</code> (A/B/C según la factura).
                  <br />
                  Ejemplos: <strong>{'{YEAR}-{NUM3}'}</strong> → 2026-001 · <strong>F-{'{SERIE}'}-{'{YEAR}'}-{'{NUM4}'}</strong> → F-A-2026-0001
                  <br />
                  Vista previa (año actual, serie A, primer número):{' '}
                  <strong style={{ color: '#2c3e50' }}>
                    {(() => {
                      const raw = (formData.formato_numero_factura || '').trim() || '{YEAR}-{NUM3}';
                      const y = new Date().getFullYear();
                      const m = raw.match(/\{NUM(\d{1,2})?\}/);
                      const pad = m && m[1] ? Math.min(12, Math.max(1, parseInt(m[1], 10))) : 3;
                      const n = String(1).padStart(pad, '0');
                      return raw
                        .replace('{YEAR}', String(y))
                        .replace('{SERIE}', 'A')
                        .replace(/\{NUM\d{0,2}\}|\{NUM\}/, n);
                    })()}
                  </strong>
                </small>
              </div>
              )}
              <div className="form-group">
                <label>Nombre del Banco</label>
                <input
                  type="text"
                  name="banco_nombre"
                  value={formData.banco_nombre}
                  onChange={handleChange}
                  placeholder="Ej: Banco Santander"
                />
              </div>
              <div className="form-group">
                <label>IBAN</label>
                <input
                  type="text"
                  name="banco_iban"
                  value={formData.banco_iban}
                  onChange={handleChange}
                  placeholder="Ej: ES12 3456 7890 1234 5678 9012"
                />
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Logo del Panel</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              disabled={uploadingLogo}
            />
            {logoPreview && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={logoPreview.startsWith('data:') ? logoPreview : `/${logoPreview}`}
                  alt="Preview del logo"
                  style={{ 
                    maxWidth: '200px', 
                    maxHeight: '100px', 
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '5px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
            <small style={{ display: 'block', color: '#666', marginTop: '5px' }}>
              Formatos permitidos: JPEG, PNG, GIF, WebP. Tamaño máximo: 2MB
            </small>
          </div>

          <div className="form-group">
            <label>Favicon del Panel</label>
            <input
              type="file"
              accept=".ico,.png,.svg,.gif,.jpeg,.jpg"
              onChange={handleFaviconChange}
              disabled={uploadingFavicon}
            />
            {faviconPreview && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={faviconPreview.startsWith('data:') ? faviconPreview : `/${faviconPreview}`}
                  alt="Preview del favicon"
                  style={{ 
                    width: '32px', 
                    height: '32px', 
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    padding: '5px',
                    objectFit: 'contain'
                  }}
                />
              </div>
            )}
            <small style={{ display: 'block', color: '#666', marginTop: '5px' }}>
              Formatos permitidos: ICO, PNG, SVG, GIF, JPEG. Tamaño máximo: 1MB
            </small>
          </div>

          <div className="form-group">
            <label>Color de Fondo del Menú</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="color"
                name="navbar_background_color"
                value={formData.navbar_background_color || '#2c3e50'}
                onChange={handleChange}
                style={{
                  width: '60px',
                  height: '40px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                name="navbar_background_color"
                value={formData.navbar_background_color || ''}
                onChange={handleChange}
                placeholder="#2c3e50"
                pattern="^#[0-9A-Fa-f]{6}$"
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    navbar_background_color: ''
                  }));
                }}
                style={{ padding: '8px 16px' }}
              >
                Restaurar por defecto
              </button>
            </div>
            <small style={{ display: 'block', color: '#666', marginTop: '5px' }}>
              Color de fondo de la barra de navegación superior
            </small>
          </div>

          <div className="form-group">
            <label>Color del Texto del Menú</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <input
                type="color"
                name="navbar_text_color"
                value={formData.navbar_text_color || '#ffffff'}
                onChange={handleChange}
                style={{
                  width: '60px',
                  height: '40px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="text"
                name="navbar_text_color"
                value={formData.navbar_text_color || ''}
                onChange={handleChange}
                placeholder="#ffffff"
                pattern="^#[0-9A-Fa-f]{6}$"
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontFamily: 'monospace'
                }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    navbar_text_color: ''
                  }));
                }}
                style={{ padding: '8px 16px' }}
              >
                Restaurar por defecto
              </button>
            </div>
            <small style={{ display: 'block', color: '#666', marginTop: '5px' }}>
              Color del texto de los enlaces y elementos del menú
            </small>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving || uploadingLogo || uploadingFavicon}>
              {(saving || uploadingLogo || uploadingFavicon) ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Perfil;

